"use client";

import { useRef, useState } from "react";
import type { CategoryPanelProps } from "../../../types/category-panel";
import { CategoryDepartmentList } from "../CategoryDepartmentList";
import { CategoryDetailCard } from "../CategoryDetailCard";
import { CategoryPanelMobile } from "../CategoryPanelMobile";
import { useIsMobile } from "@/shared/hooks/useIsMobile";
import { useViewportMaxHeight } from "../../../hooks/useViewportMaxHeight";

const CLASSIC_DROPDOWN_MIN_WIDTH = 801;
/** Altura de design do mega-menu; em tela mais baixa ele encolhe até caber. */
const CLASSIC_DROPDOWN_MAX_HEIGHT = 500;

/**
 * CategoryPanel — variante "clássico" (mega-menu).
 * Mobile (<=800px): drill-down fullscreen (CategoryPanelMobile).
 * Desktop (>800px): dropdown com a coluna de departamentos
 * (CategoryDepartmentList) + subcategorias/marcas/banner (CategoryDetailCard).
 */
export function CategoryPanelClassic({
    categories = [],
    onClose,
    anchorClassName = "left-4 top-11",
}: CategoryPanelProps) {
    const [activeId, setActiveId] = useState<string>(categories[0]?.id ?? "");
    const isMobile = useIsMobile(CLASSIC_DROPDOWN_MIN_WIDTH);
    const rowRef = useRef<HTMLDivElement>(null);
    const maxHeight = useViewportMaxHeight(rowRef, { cap: CLASSIC_DROPDOWN_MAX_HEIGHT, enabled: !isMobile });

    if (categories.length === 0) return null;

    if (isMobile) {
        return <CategoryPanelMobile categories={categories} onClose={onClose ?? (() => {})} />;
    }

    const active = categories.find((c) => c.id === activeId);

    return (
        <div
            // `rounded-b-box` (token do tema, não `rounded-b-lg` fixo): o
            // dropdown é uma "caixa" e acompanha o arredondamento da loja.
            className={`absolute z-50 hidden w-full animate-category-dropdown-in overflow-hidden rounded-b-box bg-base-100 shadow-xl motion-reduce:animate-none md:block ${anchorClassName}`}
        >
            {/* O teto fica na LINHA flex: as duas colunas esticam até ela (stretch)
                e cada uma rola por dentro. Ele vem da tela, não só dos 500px de
                design — o painel mora no header sticky, e o que passasse da
                borda inferior não aparecia nem rolando a página. */}
            <div
                ref={rowRef}
                className="flex"
                style={{ maxHeight: maxHeight ?? CLASSIC_DROPDOWN_MAX_HEIGHT }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* `rounded-bl-box` na PRÓPRIA coluna rolável: um `overflow-y-auto`
                    cria seu próprio recorte, e o `overflow-hidden` arredondado do
                    pai não é garantia de que a barra de rolagem e o fundo da linha
                    ativa (`bg-primary/5`) respeitem o canto — o Chromium deixa a
                    scrollbar "vazar" quadrada. Com o mesmo raio no container de
                    scroll, o recorte da rolagem já nasce arredondado. */}
                <CategoryDepartmentList
                    categories={categories}
                    activeId={activeId}
                    onActivate={setActiveId}
                    className="w-64 shrink-0 overflow-y-auto rounded-bl-box border-r border-base-200"
                />

                {/* `key` remonta ao trocar de departamento, resetando o
                    drill-down interno (senão o nível aprofundado de um
                    departamento vazaria pro próximo). `rounded-br-box`: mesmo
                    motivo da coluna ao lado — o card também recorta conteúdo. */}
                {active && (
                    <CategoryDetailCard
                        key={active.id}
                        category={active}
                        onClose={onClose}
                        className="rounded-br-box"
                    />
                )}
            </div>

            <div
                className="fixed inset-0 -z-10 animate-drawer-fade-in motion-reduce:animate-none"
                onClick={onClose}
                aria-hidden="true"
            />
        </div>
    );
}
